import { useMemo, useState, type CSSProperties } from "react";
import { HEBREW_LETTERS, NO_LETTER } from "../core/hebrew";
import { wordColor } from "../core/palette";
import type { ClusterResponse, PerWordResult } from "../core/types";
import type { SelectedHit } from "./HeatMap";

export interface MatrixProps {
  cluster: ClusterResponse | null;
  selected: SelectedHit | null;
  mutedWords?: ReadonlySet<number>;
  // Linear-ELS results from the heatmap search. Used to overlay every hit
  // whose letters fall inside the matrix window — this keeps the matrix
  // and the Torah-text highlights in agreement (any colored letter in
  // the text appears colored in the matrix at the corresponding cell,
  // even when the hit's skip doesn't align with the matrix width).
  results?: PerWordResult[];
  dMin?: number;
  dMax?: number;
}

const MIN_MATRIX_FONT = 10;
const MAX_MATRIX_FONT = 36;
const DEFAULT_MATRIX_FONT = 18;

interface CellOverlay {
  // Background color overrides come from cluster matches.
  bg?: string;
  // Outline color comes from the seed.
  outline?: string;
}

export function Matrix(props: MatrixProps): JSX.Element {
  const { cluster, selected, mutedWords, results, dMin, dMax } = props;
  const [fontSize, setFontSize] = useState<number>(DEFAULT_MATRIX_FONT);

  const overlays = useMemo(() => {
    if (!cluster) return null;
    const win = cluster.window;
    const cells: CellOverlay[] = new Array(win.rows * win.width)
      .fill(null)
      .map(() => ({}));

    if (selected && !(mutedWords && mutedWords.has(selected.wordIndex))) {
      const seedColor = wordColor(selected.wordIndex);
      for (let k = 0; k < win.seedLen; k++) {
        const row = win.seedDown
          ? win.seedRow + k
          : win.seedRow + (win.seedLen - 1 - k);
        const idx = row * win.width + win.seedCol;
        if (idx >= 0 && idx < cells.length) {
          cells[idx]!.outline = seedColor;
        }
      }
    }

    // Linear-ELS overlay: project every hit whose letter positions fall
    // inside the visible window onto the matrix grid. The matrix is laid
    // out row-major over the corpus starting at `win.origin` with stride
    // `win.width`, so a corpus position `pos` lands at
    //   row = floor((pos - origin) / width)
    //   col = (pos - origin) % width
    // when 0 ≤ row < win.rows. This guarantees that any letter colored in
    // the Torah-text pane is also colored in the matrix.
    if (results && dMin !== undefined && dMax !== undefined) {
      const winEnd = win.origin + win.rows * win.width;
      for (let wi = 0; wi < results.length; wi++) {
        if (mutedWords && mutedWords.has(wi)) continue;
        const color = wordColor(wi, 0.45);
        const hits = results[wi]!.hits;
        for (let hi = 0; hi < hits.length; hi++) {
          const h = hits[hi]!;
          const absD = Math.abs(h.d);
          if (absD < dMin || absD > dMax) continue;
          for (let k = 0; k < h.len; k++) {
            const pos = h.p + k * h.d;
            if (pos < win.origin || pos >= winEnd) continue;
            const off = pos - win.origin;
            const row = Math.floor(off / win.width);
            const col = off - row * win.width;
            const idx = row * win.width + col;
            if (idx >= 0 && idx < cells.length && !cells[idx]!.bg) {
              cells[idx]!.bg = color;
            }
          }
        }
      }
    }

    // Cluster-scan overlay: re-detect each search word in any of the 8
    // grid directions of the matrix. Useful for spotting cross/diagonal
    // patterns that the linear ELS pass would miss.
    for (const m of cluster.matches) {
      if (m.wordIndex < 0) continue;
      if (mutedWords && mutedWords.has(m.wordIndex)) continue;
      const color = wordColor(m.wordIndex, 0.45);
      for (let k = 0; k < m.len; k++) {
        const r = m.row + k * m.dr;
        const c = m.col + k * m.dc;
        const idx = r * win.width + c;
        if (idx >= 0 && idx < cells.length) cells[idx]!.bg = color;
      }
    }
    return cells;
  }, [cluster, selected, mutedWords, results, dMin, dMax]);

  if (!cluster) {
    return (
      <div className="matrix-empty">
        Click a heatmap cell to see its 2D Torah-code matrix.
      </div>
    );
  }

  const win = cluster.window;
  const zoomIn = () =>
    setFontSize((s) => Math.min(MAX_MATRIX_FONT, s + 2));
  const zoomOut = () =>
    setFontSize((s) => Math.max(MIN_MATRIX_FONT, s - 2));
  const zoomReset = () => setFontSize(DEFAULT_MATRIX_FONT);

  return (
    <>
      <div className="matrix-toolbar">
        <span>
          width = {win.width} · {win.rows} rows · {cluster.matches.length}{" "}
          cluster match{cluster.matches.length === 1 ? "" : "es"}
        </span>
        <span className="toolbar-zoom">
          <button onClick={zoomOut} aria-label="Zoom out">−</button>
          <span className="zoom-readout">{fontSize}px</span>
          <button onClick={zoomIn} aria-label="Zoom in">+</button>
          <button onClick={zoomReset} aria-label="Reset zoom">reset</button>
        </span>
      </div>
      <div className="matrix-container">
        <div
          className="matrix-grid"
          dir="rtl"
          lang="he"
          style={
            {
              gridTemplateColumns: `repeat(${win.width}, 1.4em)`,
              "--matrix-zoom": `${fontSize}px`,
            } as CSSProperties
          }
        >
        {Array.from({ length: win.rows * win.width }, (_, idx) => {
          const code = win.letters[idx]!;
          const overlay = overlays?.[idx] ?? {};
          if (code === NO_LETTER) {
            return <span key={idx} className="matrix-cell empty" />;
          }
          return (
            <span
              key={idx}
              className="matrix-cell"
              style={{
                background: overlay.bg,
                outline: overlay.outline ? `2px solid ${overlay.outline}` : undefined,
              }}
            >
              {HEBREW_LETTERS[code]}
            </span>
          );
        })}
        </div>
      </div>
    </>
  );
}
